<script setup lang="ts">
// 外部AIエージェント（Claude Code 等）の OAuth 同意画面（#548 A2）。
// user MCP の /authorize がここへ 302 で飛ばす。同意 UI は共通 Dialog コンポーネントを使う。
//
// 認証は **このページ自身で**ハンドリングする（共通 `authenticated` ミドルウェアは
// `/signin` へ飛ばす際に元 URL=OAuth パラメータを捨ててしまい、ログイン後に戻れないため）。
// 未ログインなら fullPath を sessionStorage に退避 → `/signin` → ログイン成功後に
// `useAuthFlow` / google callback がそこへ戻す。
import { POST_LOGIN_REDIRECT_KEY } from '~/composables/auth/postLoginRedirect'
import { useUserMcpApi } from '~/composables/api/useUserMcpApi'

definePageMeta({
  layout: 'default',
})

const route = useRoute()
const router = useRouter()
const { grant } = useUserMcpApi()

// 認証ゲート。SSR では localStorage が無く未認証扱いになるため、hydration 後（onMounted）に評価する。
const authReady = ref(false)
onMounted(() => {
  const { isAuthenticated } = useAuthInfo()
  if (!isAuthenticated) {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, route.fullPath)
    navigateTo('/signin')
    return
  }
  authReady.value = true
})

const redirectUri = route.query.redirect_uri as string | undefined
const codeChallenge = route.query.code_challenge as string | undefined
const codeChallengeMethod = (route.query.code_challenge_method as string) || 'S256'
const scope = (route.query.scope as string) || 'user-mcp/read user-mcp/write'
const state = (route.query.state as string) || null

const scopeLabels = computed(() =>
  scope
    .split(/\s+/)
    .filter(Boolean)
    .map((s) => {
      if (s === 'user-mcp/read') return '魔導書を読む'
      if (s === 'user-mcp/write') return '魔導書を書き換える'
      return s
    }),
)

const invalid = computed(
  () => !redirectUri || !codeChallenge || codeChallengeMethod !== 'S256',
)

const dialogOpen = ref(true)

const onApprove = async () => {
  if (invalid.value || !redirectUri || !codeChallenge) return
  try {
    const res = await grant({
      redirect_uri: redirectUri,
      code_challenge: codeChallenge,
      code_challenge_method: codeChallengeMethod,
      scope,
      state,
    })
    window.location.href = res.redirectTo
  }
  catch {
    useGameStateStore().notificationQueue.push(
      '接続の許可に失敗しました。エージェントからやり直してください。',
    )
    router.push('/cradle')
  }
}

const onDeny = () => {
  if (redirectUri) {
    const sep = redirectUri.includes('?') ? '&' : '?'
    const q = new URLSearchParams({ error: 'access_denied' })
    if (state) q.set('state', state)
    window.location.href = `${redirectUri}${sep}${q.toString()}`
    return
  }
  router.push('/cradle')
}
</script>

<template>
  <!-- 認証確認が済むまでは Dialog（羊皮紙）を出さない。未ログインは即 /signin（通常デザイン）。 -->
  <div
    v-if="!authReady"
    class="connect-checking"
  >
    <p>確認しています…</p>
  </div>

  <Dialog
    v-else-if="invalid"
    v-model="dialogOpen"
    title="接続できません"
    :cancel-button="{ title: 'ゆりかごに戻る' }"
    @cancel="router.push('/cradle')"
  >
    リクエストが不正です（redirect_uri / PKCE が不足）。
    お使いのエージェントからもう一度接続を開始してください。
  </Dialog>

  <Dialog
    v-else
    v-model="dialogOpen"
    title="外部エージェントの接続"
    :cancel-button="{ title: '許可しない' }"
    :submit-button="{ title: '許可する' }"
    @cancel="onDeny"
    @submit="onApprove"
  >
    <p>お使いの AI エージェントに、あなたの魔導書への以下の操作を許可しますか？</p>
    <ul class="connect-scopes">
      <li
        v-for="label in scopeLabels"
        :key="label"
      >
        {{ label }}
      </li>
    </ul>
    <p class="connect-note">
      許可されるのは「羽根筆のゆりかご」のあなたの白い魔導書だけです。
      エンコーディア探索の魔導書（写経）は読み書きできません。
    </p>
  </Dialog>
</template>

<style scoped>
.connect-checking {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  color: rgba(0, 0, 0, 0.5);
}

.connect-scopes {
  list-style: none;
  padding: 0;
  /* リストの下に空白行ぶんの余白を入れる。 */
  margin: 12px 0 24px;
  text-align: center;
}

.connect-scopes li {
  font-weight: bold;
}

.connect-scopes li::before {
  content: "\2714";
  margin-right: 6px;
  color: brown;
}

.connect-note {
  font-size: 0.9em;
  opacity: 0.7;
  margin-top: 8px;
}
</style>
